import { useState, useEffect } from 'react';
import HI from '../components/Icons';
import { validateEmail, validatePassword, validateName } from '../utils/validation';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../utils/constants';
import { conversionFunnel, formTracking } from '../utils/analytics';

const TEST_ACCOUNTS = [
  'athik@cocafe.host',
  'priya@cocafe.host',
  'raj@cocafe.host',
  'vikram@cocafe.host',
  'neha@cocafe.host',
  'arun@cocafe.host',
];

const IS_DEV = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;

function getPasswordStrength(pwd) {
  if (!pwd) return { score: 0, label: '', color: '#999' };
  let score = 0;
  if (pwd.length >= 6) score++;
  if (pwd.length >= 10) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  const strength = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'][Math.min(score - 1, 4)];
  const colors = ['#e74c3c', '#e67e22', '#f39c12', '#27ae60', '#16a085'];
  return { score, label: strength, color: colors[Math.min(score - 1, 4)] };
}

function PasswordRequirements({ password }) {
  const checks = [
    { label: 'At least 6 characters', met: password.length >= 6 },
    { label: 'At least 1 uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'At least 1 number', met: /[0-9]/.test(password) },
  ];
  return (
    <div style={{ fontSize: 12, marginTop: 8, marginBottom: 12, color: 'var(--ink-2)' }}>
      {checks.map((check, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: check.met ? '#27ae60' : '#ccc' }}>
            {check.met ? '✓' : '○'}
          </span>
          <span style={{ color: check.met ? '#27ae60' : 'var(--ink-2)' }}>
            {check.label}
          </span>
        </div>
      ))}
    </div>
  );
}

export function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [numDesks, setNumDesks] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('host');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Track page views and form starts
  useEffect(() => {
    if (isSignup) {
      conversionFunnel.trackSignupView();
      formTracking.trackFormFocus('signup-form');
    } else {
      conversionFunnel.trackLoginPageView();
      formTracking.trackFormFocus('login-form');
    }
  }, [isSignup]);

  const validateField = (field, value) => {
    if (field === 'email') {
      const validation = validateEmail(value);
      return !validation.valid ? validation.error : '';
    }
    if (field === 'password') {
      const validation = validatePassword(value);
      return !validation.valid ? validation.error : '';
    }
    if (field === 'name') {
      const validation = validateName(value, 'Full Name');
      return !validation.valid ? validation.error : '';
    }
    if (field === 'confirmPassword' && isSignup) {
      if (value !== password) return 'Passwords do not match';
    }
    return '';
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, field === 'email' ? email : field === 'password' ? password : field === 'confirmPassword' ? confirmPassword : name);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    formTracking.trackFieldInput(isSignup ? 'signup-form' : 'login-form', 'email');
    if (touched.email) {
      const error = validateField('email', e.target.value);
      setFieldErrors((prev) => ({ ...prev, email: error }));
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    formTracking.trackFieldInput(isSignup ? 'signup-form' : 'login-form', 'password');
    if (touched.password) {
      const error = validateField('password', e.target.value);
      setFieldErrors((prev) => ({ ...prev, password: error }));
    }
    if (confirmPassword && isSignup) {
      const confirmError = e.target.value !== confirmPassword ? 'Passwords do not match' : '';
      setFieldErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const handleConfirmChange = (e) => {
    setConfirmPassword(e.target.value);
    formTracking.trackFieldInput('signup-form', 'confirmPassword');
    if (touched.confirmPassword) {
      const error = validateField('confirmPassword', e.target.value);
      setFieldErrors((prev) => ({ ...prev, confirmPassword: error }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const newFieldErrors = {};
    newFieldErrors.email = validateField('email', email);
    newFieldErrors.password = validateField('password', password);
    if (isSignup) {
      newFieldErrors.name = validateField('name', name);
      newFieldErrors.confirmPassword = validateField('confirmPassword', confirmPassword);
    }

    setFieldErrors(newFieldErrors);
    setTouched({ email: true, password: true, name: true, confirmPassword: true });

    if (Object.values(newFieldErrors).some((err) => err)) {
      setError('Please fix the errors above');
      formTracking.trackFormSubmission(isSignup ? 'signup-form' : 'login-form', false);
      return;
    }

    if (isSignup && !agreeTerms) {
      setError('Please agree to the terms and conditions');
      formTracking.trackFormSubmission('signup-form', false);
      return;
    }

    setLoading(true);

    // Track login/signup attempt
    if (isSignup) {
      conversionFunnel.trackSignupStarted();
    } else {
      conversionFunnel.trackLoginAttempt(email);
    }

    try {
      if (isSignup) {
        await onLogin({ email, password, name, role, isSignup: true });
        conversionFunnel.trackSignupCompletion();
        formTracking.trackFormSubmission('signup-form', true);
      } else {
        await onLogin({ email, password, isSignup: false });
        conversionFunnel.trackLoginSuccess(email, 'email');
        formTracking.trackFormSubmission('login-form', true);
      }
    } catch (err) {
      const errorMsg =
        err.message === 'Incorrect password'
          ? 'Incorrect password. Please try again.'
          : err.message === 'Email not found'
          ? 'No account found with this email. Would you like to sign up?'
          : err.message || ERROR_MESSAGES.GENERIC;
      setError(errorMsg);

      // Track errors
      if (isSignup) {
        conversionFunnel.trackLoginError('signup_' + err.message?.toLowerCase().replace(/\s+/g, '_'));
        formTracking.trackFormSubmission('signup-form', false);
      } else {
        conversionFunnel.trackLoginError(err.message?.toLowerCase().replace(/\s+/g, '_'));
        formTracking.trackFormSubmission('login-form', false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    conversionFunnel.trackLoginAttempt('social_' + provider.toLowerCase());

    if (provider === 'Twitter') {
      // TODO: Implement Twitter OAuth
      alert('Twitter login coming soon - OAuth integration needed');
    } else if (provider === 'Google') {
      alert('Google login coming soon - OAuth integration needed');
    } else if (provider === 'Facebook') {
      alert('Facebook login coming soon - OAuth integration needed');
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    // Navigate to forgot password page instead of alert
    window.location.href = '/forgot-password';
  };

  const dismissError = () => setError('');

  const useDemoAccount = (demoEmail) => {
    setEmail(demoEmail);
    setFieldErrors((prev) => ({ ...prev, email: '' }));
    setTouched((prev) => ({ ...prev, email: true }));
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        minHeight: '100vh',
        background: 'var(--surface)',
        gap: 0,
        '@media (max-width: 768px)': {
          gridTemplateColumns: '1fr',
        },
      }}
    >
      {/* Skip to main content link */}
      <a
        href="#main-auth-form"
        style={{
          position: 'absolute',
          top: -40,
          left: 0,
          background: 'var(--green)',
          color: '#fff',
          padding: '8px 16px',
          zIndex: 1000,
        }}
        onFocus={(e) => {
          e.target.style.top = '0';
        }}
        onBlur={(e) => {
          e.target.style.top = '-40px';
        }}
      >
        Skip to authentication form
      </a>
      {/* Left Side - Brand Section */}
      <div
        style={{
          background: 'linear-gradient(135deg, #8B6F47 0%, #6F4E37 100%)',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 60,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Decoration */}
        <div
          style={{
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '500px',
            height: '500px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-30%',
            left: '-10%',
            width: '400px',
            height: '400px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
            zIndex: 1,
          }}
        />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>☕</div>
          <h1 style={{ fontSize: 42, fontWeight: 800, marginBottom: 12, lineHeight: 1.2 }}>CoCafe Host</h1>
          <p style={{ fontSize: 16, lineHeight: 1.6, opacity: 0.95, maxWidth: 320, margin: '0 auto' }}>
            Manage your workspace, grow your business, and connect with professionals looking for the perfect desk.
          </p>
        </div>
      </div>

      {/* Right Side - Content and Login Form */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '40px 30px',
          overflowY: 'auto',
          '@media (max-width: 768px)': {
            padding: '30px 20px',
          },
        }}
      >
        {/* SIGNUP CTA SECTION - FIX #1 */}
        {isSignup && (
          <div
            style={{
              width: '100%',
              maxWidth: 400,
              background: 'linear-gradient(135deg, #f5f3f0 0%, #e8e4df 100%)',
              padding: 30,
              borderRadius: 'var(--r)',
              textAlign: 'center',
              marginBottom: 30,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <h3 style={{ fontSize: 22, fontWeight: 700, color: '#333', marginBottom: 10, margin: '0 0 10px 0' }}>
              New to CoCafe Host?
            </h3>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 20, margin: '0 0 20px 0' }}>
              Start listing your workspace and earn money today
            </p>
            <button
              type="button"
              onClick={() => {
                setIsSignup(true);
                setError('');
                setFieldErrors({});
                setTouched({});
              }}
              style={{
                width: '100%',
                padding: '14px 20px',
                fontSize: 15,
                fontWeight: 700,
                background: 'var(--green)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--r-sm)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => (e.target.style.background = 'var(--green-ink)')}
              onMouseLeave={(e) => (e.target.style.background = 'var(--green)')}
            >
              Create Your Account
            </button>
          </div>
        )}

        {/* HOW IT WORKS SECTION - FIX #2 */}
        {isSignup && (
          <div
            style={{
              width: '100%',
              maxWidth: 400,
              marginBottom: 30,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#333', marginBottom: 20, textAlign: 'center' }}>
              How It Works
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { num: '1', title: 'List Your Space', desc: 'Add photos, pricing, and availability' },
                { num: '2', title: 'Manage Bookings', desc: 'Confirm bookings and handle payments' },
                { num: '3', title: 'Grow Your Business', desc: 'Earn ₹500-1000/day, build customers' },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 12 }}>
                  <div
                    style={{
                      background: '#8B7355',
                      color: '#fff',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  >
                    {step.num}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 4 }}>
                      {step.title}
                    </div>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      {step.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TRUST SIGNALS SECTION - FIX #3 */}
        {isSignup && (
          <div
            style={{
              width: '100%',
              maxWidth: 400,
              background: '#f9f7f5',
              padding: 24,
              borderRadius: 'var(--r)',
              marginBottom: 30,
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 16, textAlign: 'center', margin: '0 0 16px 0' }}>
              Trusted by Hosts Across India
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {[
                { number: '2,400+', label: 'Hosts' },
                { number: '₹8.5 Cr', label: 'Earned' },
                { number: '150K+', label: 'Bookings' },
                { number: '4.8★', label: 'Rating' },
              ].map((stat, i) => (
                <div key={i} style={{ textAlign: 'center', padding: 12 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#8B7355', marginBottom: 4 }}>
                    {stat.number}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #ddd', paddingTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 10 }}>
                Success Stories:
              </div>
              {[
                { quote: 'Made ₹2.5L in 3 months!', author: 'Priya Singh, Mumbai' },
                { quote: 'Best use of my extra space', author: 'Rohan Menon, Bangalore' },
              ].map((testimonial, i) => (
                <div key={i} style={{ marginBottom: i === 0 ? 10 : 0 }}>
                  <div style={{ fontSize: 12, color: '#333', fontStyle: 'italic', marginBottom: 4 }}>
                    "{testimonial.quote}"
                  </div>
                  <div style={{ fontSize: 11, color: '#8B7355', fontWeight: 600 }}>
                    — {testimonial.author}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            width: '100%',
            maxWidth: 400,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r)',
            padding: 40,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <h2 id="form-heading" style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: 'var(--ink)' }}>
              {isSignup ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0 }}>
              {isSignup
                ? 'Join CoCafe and start managing your workspace'
                : 'Sign in to manage your workspace listings'}
            </p>
          </div>

          {/* Security Badge */}
          {!isSignup && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '10px 12px',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: 'var(--r-sm)',
                marginBottom: 20,
              }}
            >
              <span style={{ color: '#22c55e', fontSize: 16 }}>🔒</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#16a34a' }}>
                Secure login • Your data is encrypted
              </span>
            </div>
          )}

          {/* Error Message - Dismissible */}
          {error && (
            <div
              role="alert"
              style={{
                padding: '12px 14px',
                background: 'var(--red-weak)',
                border: '1px solid var(--red)',
                borderRadius: 'var(--r-sm)',
                color: 'var(--red)',
                fontSize: 13,
                marginBottom: 20,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                animation: 'slideDown 0.3s var(--ease)',
              }}
            >
              <span style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }}>⚠️</span>
              <div style={{ flex: 1 }}>{error}</div>
              <button
                type="button"
                onClick={dismissError}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--red)',
                  cursor: 'pointer',
                  fontSize: 16,
                  padding: 0,
                  flexShrink: 0,
                }}
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          {/* Dev Mode - Demo Accounts */}
          {IS_DEV && !isSignup && (
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--surface-2)', borderRadius: 'var(--r-sm)', fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--ink-2)' }}>Demo Accounts (Dev Mode):</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {TEST_ACCOUNTS.map((account) => (
                  <button
                    key={account}
                    type="button"
                    onClick={() => useDemoAccount(account)}
                    style={{
                      padding: '6px 8px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-sm)',
                      fontSize: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: 'var(--green)',
                      fontFamily: 'monospace',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => (e.target.style.background = 'var(--green-weak)')}
                    onMouseLeave={(e) => (e.target.style.background = 'var(--surface)')}
                  >
                    {account}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>Password: demo123</div>
            </div>
          )}

          {/* Form */}
          <form id="main-auth-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }} role="form" aria-labelledby="form-heading">
            {/* Name Field - Signup Only */}
            {isSignup && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (touched.name) {
                      const error = validateField('name', e.target.value);
                      setFieldErrors((prev) => ({ ...prev, name: error }));
                    }
                  }}
                  onBlur={() => handleBlur('name')}
                  placeholder="John Doe"
                  style={{
                    padding: '11px 13px',
                    fontSize: 15,
                    border: fieldErrors.name ? '2px solid var(--red)' : '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--green)';
                    e.target.style.outline = '2px solid rgba(139, 111, 71, 0.1)';
                  }}
                  onBlurCapture={(e) => {
                    e.target.style.outline = 'none';
                    if (!fieldErrors.name) e.target.style.borderColor = 'var(--border)';
                  }}
                  aria-label="Full name"
                  aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                />
                {fieldErrors.name && (
                  <div id="name-error" style={{ fontSize: 12, color: 'var(--red)', display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span>×</span> {fieldErrors.name}
                  </div>
                )}
              </div>
            )}

            {/* Business Name - Signup Only */}
            {isSignup && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                  Workspace/Business Name *
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g., Coffee Lounge, Coworking Space"
                  style={{
                    padding: '11px 13px',
                    fontSize: 15,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--green)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                  }}
                  aria-label="Business name"
                />
              </div>
            )}

            {/* Number of Desks - Signup Only */}
            {isSignup && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                  Number of Desks/Seats *
                </label>
                <input
                  type="number"
                  value={numDesks}
                  onChange={(e) => setNumDesks(e.target.value)}
                  placeholder="e.g., 10"
                  min="1"
                  style={{
                    padding: '11px 13px',
                    fontSize: 15,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--green)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                  }}
                  aria-label="Number of desks"
                />
              </div>
            )}

            {/* Address - Signup Only */}
            {isSignup && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                  Business Address *
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g., 123 Main St, City"
                  style={{
                    padding: '11px 13px',
                    fontSize: 15,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--green)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                  }}
                  aria-label="Business address"
                />
              </div>
            )}

            {/* Phone - Signup Only */}
            {isSignup && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g., +1 (555) 123-4567"
                  autoComplete="tel"
                  inputMode="tel"
                  style={{
                    padding: '11px 13px',
                    fontSize: 15,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--green)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                  }}
                  aria-label="Phone number"
                />
              </div>
            )}

            {/* Email Field */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                Email *
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => handleBlur('email')}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  style={{
                    width: '100%',
                    padding: '11px 40px 11px 13px',
                    fontSize: 15,
                    border: fieldErrors.email ? '2px solid var(--red)' : '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--green)';
                    e.target.style.outline = '2px solid rgba(139, 111, 71, 0.1)';
                  }}
                  onBlurCapture={(e) => {
                    e.target.style.outline = 'none';
                    if (!fieldErrors.email) e.target.style.borderColor = 'var(--border)';
                  }}
                  aria-label="Email address"
                  aria-describedby={fieldErrors.email ? 'email-error' : 'email-hint'}
                />
                {!fieldErrors.email && touched.email && email && (
                  <span style={{ position: 'absolute', right: 12, color: '#27ae60', fontSize: 16 }}>✓</span>
                )}
              </div>
              {fieldErrors.email && (
                <div id="email-error" style={{ fontSize: 12, color: 'var(--red)', display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span>×</span> {fieldErrors.email}
                </div>
              )}
              {!fieldErrors.email && (
                <div id="email-hint" style={{ fontSize: 11, color: 'var(--muted)' }}>
                  We'll use this to sign you in securely
                </div>
              )}
            </div>

            {/* Password Field */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                  Password *
                </label>
                {!isSignup && (
                  <a
                    href="/forgot-password"
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--green)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      padding: '4px 8px',
                      transition: 'all 0.2s',
                      display: 'inline-block',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                      e.currentTarget.style.color = 'var(--green-ink)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                      e.currentTarget.style.color = 'var(--green)';
                    }}
                  >
                    🔐 Forgot password?
                  </a>
                )}
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => handleBlur('password')}
                  placeholder="••••••••"
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  style={{
                    width: '100%',
                    padding: '11px 40px 11px 13px',
                    fontSize: 15,
                    border: fieldErrors.password ? '2px solid var(--red)' : '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--green)';
                    e.target.style.outline = '2px solid rgba(139, 111, 71, 0.1)';
                  }}
                  onBlurCapture={(e) => {
                    e.target.style.outline = 'none';
                    if (!fieldErrors.password) e.target.style.borderColor = 'var(--border)';
                  }}
                  aria-label="Password"
                  aria-describedby={fieldErrors.password ? 'password-error' : 'password-hint'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 12,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--muted)',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                  }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {isSignup && password && <PasswordRequirements password={password} />}
              {isSignup && password && (
                <div style={{ marginTop: 0 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: 4,
                          background: getPasswordStrength(password).score >= i ? getPasswordStrength(password).color : '#ddd',
                          borderRadius: 2,
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: getPasswordStrength(password).color, fontWeight: 600 }}>
                    Strength: {getPasswordStrength(password).label}
                  </div>
                </div>
              )}
              {fieldErrors.password && (
                <div id="password-error" style={{ fontSize: 12, color: 'var(--red)', display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span>×</span> {fieldErrors.password}
                </div>
              )}
              {!fieldErrors.password && (
                <div id="password-hint" style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {isSignup ? 'Minimum 6 characters' : 'Your secure password'}
                </div>
              )}
            </div>

            {/* Confirm Password - Signup Only */}
            {isSignup && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                  Confirm Password *
                </label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={handleConfirmChange}
                    onBlur={() => handleBlur('confirmPassword')}
                    placeholder="••••••••"
                    style={{
                      width: '100%',
                      padding: '11px 40px 11px 13px',
                      fontSize: 15,
                      border: fieldErrors.confirmPassword ? '2px solid var(--red)' : '1px solid var(--border)',
                      borderRadius: 'var(--r-sm)',
                      fontFamily: 'inherit',
                      transition: 'all 0.2s',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--green)';
                      e.target.style.outline = '2px solid rgba(139, 111, 71, 0.1)';
                    }}
                    onBlurCapture={(e) => {
                      e.target.style.outline = 'none';
                      if (!fieldErrors.confirmPassword) e.target.style.borderColor = 'var(--border)';
                    }}
                    aria-label="Confirm password"
                    aria-describedby={fieldErrors.confirmPassword ? 'confirm-error' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    style={{
                      position: 'absolute',
                      right: 12,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--muted)',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                    }}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <div id="confirm-error" style={{ fontSize: 12, color: 'var(--red)', display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span>×</span> {fieldErrors.confirmPassword}
                  </div>
                )}
              </div>
            )}

            {/* Account Type - Host Only (Signup Only) */}
            {isSignup && (
              <div style={{ padding: '12px 14px', background: 'var(--green-weak)', border: '1px solid var(--green)', borderRadius: 'var(--r-sm)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-ink)' }}>✓ Host Account</div>
                <div style={{ fontSize: 12, color: 'var(--green-ink)', marginTop: 4, opacity: 0.85 }}>Manage your workspace and bookings</div>
              </div>
            )}

            {/* Terms Checkbox - Signup Only */}
            {isSignup && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  style={{ marginTop: 2, cursor: 'pointer' }}
                  aria-label="Agree to terms and conditions"
                />
                <span>
                  I agree to CoCafe{' '}
                  <button
                    type="button"
                    onClick={() => alert('Terms coming soon')}
                    style={{
                      color: 'var(--green)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 'inherit',
                    }}
                  >
                    terms and conditions
                  </button>
                </span>
              </label>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || (isSignup && !agreeTerms)}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                fontWeight: 600,
                marginTop: 8,
                background: loading || (isSignup && !agreeTerms) ? '#ccc' : 'var(--green)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--r-sm)',
                cursor: loading || (isSignup && !agreeTerms) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading || (isSignup && !agreeTerms) ? 0.6 : 1,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = 'var(--green-ink)')}
              onMouseLeave={(e) => !loading && (e.target.style.background = 'var(--green)')}
              aria-label={isSignup ? 'Create account' : 'Sign in'}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <span style={{ marginRight: 8, display: 'inline-block', animation: 'spin 1s linear infinite' }}>⟳</span>
                  {isSignup ? 'Creating account...' : 'Signing in...'}
                </>
              ) : isSignup ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 400 }}>Or sign in with</span>
          </div>

          {/* Social Login Buttons - VISIBLE & PROMINENT */}
          <div style={{ marginBottom: 24, width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { name: 'Google', icon: 'G', bgColor: '#4285F4', textColor: '#fff' },
              { name: 'Facebook', icon: 'f', bgColor: '#1877F2', textColor: '#fff' },
              { name: 'Twitter', icon: 'X', bgColor: '#000000', textColor: '#fff' },
            ].map((provider) => (
              <button
                key={provider.name}
                type="button"
                onClick={() => handleSocialLogin(provider.name)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 'var(--r-sm)',
                  border: 'none',
                  background: provider.bgColor,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s ease',
                  fontSize: 15,
                  fontWeight: 600,
                  color: provider.textColor,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
                title={`Sign in with ${provider.name}`}
                aria-label={`Sign in with ${provider.name}`}
              >
                <span style={{ fontSize: 18, fontWeight: 700 }}>{provider.icon}</span>
                <span>Sign in with {provider.name}</span>
              </button>
            ))}
          </div>

          {/* Toggle Link */}
          <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>
            {isSignup ? (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignup(false);
                    setError('');
                    setFieldErrors({});
                    setTouched({});
                  }}
                  style={{
                    color: 'var(--green)',
                    fontWeight: 600,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    textDecoration: 'underline',
                  }}
                  aria-label="Switch to sign in"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignup(true);
                    setError('');
                    setFieldErrors({});
                    setTouched({});
                  }}
                  style={{
                    color: 'var(--green)',
                    fontWeight: 600,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    textDecoration: 'underline',
                  }}
                  aria-label="Switch to sign up"
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns: '1fr 1fr'"] {
            grid-template-columns: 1fr !important;
          }
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default LoginScreen;
